import React, {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  HStack,
  IconButton,
  InputGroup,
  InputRightElement,
  Stack,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ReplayRounded, CheckCircleOutline } from "@material-ui/icons";
import Back from "assets/svg/back-arrow.svg";
import Add from "assets/svg/icons-new/add.svg";
import Arrow from "assets/svg/icons-new/arrow.svg";
// import Bank from "assets/svg/icons-new/banks.svg";
// import Edit from "assets/svg/icons-new/edit.svg";
import Master from "assets/svg/icons-new/master.svg";
// import Refer from "assets/svg/icons-new/refer.svg";
import { ReactComponent as NoDataIcon } from "assets/svg/noData.svg";
// import VerificationIcon from "assets/svg/verification.svg";
import Button from "components/Button/Button";
import Header from "components/dashHeader";
import Input from "components/Input/Input";
// import ProfileShortInfo from "components/ProfileShortInfo/ProfileShortInfo";
import AccountSidebar from "components/AccountSidebar/AccountSidebar";
import Select from "components/Select/Select";
import Sidemenu from "components/sidemenu";
// import SupportBox from "components/support";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
//css
import "pages/styles/dashboard/Account.css";

import {
  useAddBankMutation,
  useDeleteBankMutation,
  useGetBanksByCountryQuery,
  useGetBanksDetailsQuery,
  useResolveAccountMutation,
} from "services/bankApi";
import { useStore } from "store/index";
import { del, get, post } from "util/axiosLib";
import { logger } from "util/logger";

const AddBankForm = ({ onAdded }) => {
  const { store } = useStore();
  const userData = store?.userData;
  const history = useHistory();
  const location = useLocation();
  console.log(location, "LOCATION");
  const toast = useToast();
  const [state, setState] = useState({});

  const country = userData && String(userData.country).toLowerCase();

  const countryCodeMap = {
    nigeria: "ng",
    ghana: "gh",
  };

  const { data: banks, isLoading: isBankLoading } = useGetBanksByCountryQuery(
    countryCodeMap[country],
    { skip: !country }
  );

  const [
    addBank,
    {
      data: addBankData,
      isLoading: isAddingBank,
      error: addBankError,
      isError: addBankHasError,
      isSuccess: isAddBankSuccess,
    },
  ] = useAddBankMutation();

  const [
    resolveBank,
    { data: resolvedBank, isLoading: isResolvingBank, error, isError },
  ] = useResolveAccountMutation();

  // eslint-disable-next-line
  const debouncedBankResolver = useCallback(
    debounce((bankAccountNumber) => {
      return (
        state?.bankCode &&
        resolveBank({
          bankCode: state.bankCode,
          bankAccountNumber,
        })
      );
    }, 2000),

    [state?.bankCode, resolveBank]
  );

  useEffect(() => {
    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Oops an error occurred",
        description: error?.data?.error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [isError, error, toast]);

  useEffect(() => {
    if (addBankHasError && addBankError) {
      toast({
        position: "bottom-right",
        title: "Oops an error occurred",
        description: addBankError?.data?.error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    if (isAddBankSuccess && addBankData) {
      toast({
        position: "bottom-right",
        title: "Success",
        description: "Your bank has been successfully added",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onAdded(addBankData);

      if (location.search === "?redirect") {
        setTimeout(() => {
          history.replace("/dashboard/homewithdraw");
        }, 2000);
      }
    }
  }, [
    isAddBankSuccess,
    addBankHasError,
    addBankError,
    toast,
    addBankData,
    onAdded,
    location,
    history,
  ]);

  useEffect(() => {
    if (resolvedBank)
      setState((prev) => ({
        ...prev,
        bankAccountName: resolvedBank.account_name,
      }));
  }, [resolvedBank]);

  const getBankList = (data) => {
    const sort = (list) =>
      sortBy(list, ({ bankName }) => bankName?.toLowerCase());
    if (country === "nigeria" && data) return sort(data["bankPayloads"]);
    if (country === "ghana" && data) return sort(data["ghanaBankPayloads"]);
  };

  const handleSelection = (e) => {
    const [bankCode, bankName] = String(e.target.value).split("-");
    setState({ ...state, bankCode, bankName });
  };

  const handleChanges = (input) => {
    setState({ ...state, ...input });
  };

  const handleAccountNumber = (e) => {
    setState({ ...state, bankAccountNumber: e.target.value });

    if (e.target.value.length >= 7) {
      country === "nigeria" && debouncedBankResolver(e.target.value);
    }
  };

  const handleAddBank = () => {
    addBank({
      ...state,
      address: state?.address ?? country ?? "",
      country: countryCodeMap[country],
    });
  };

  const isDisabled = () =>
    !(
      state.bankCode &&
      state.bankAccountNumber &&
      state.bankAccountName &&
      state.bankName
    ) || isAddingBank;

  return (
    <Box as="form" mt="40px">
      <Container maxW="md" m="0">
        <Stack gridGap="14px">
          <Select
            isLoading={isBankLoading}
            label="Bank Name"
            value={`${state.bankCode}-${state.bankName}`}
            onChange={handleSelection}
            placeholder="Select bank"
          >
            {getBankList(banks)?.map((bank, i) => (
              <option key={i} value={`${bank.bankCode}-${bank.bankName}`}>
                {bank.bankName}
              </option>
            ))}
          </Select>
          {country === "ghana" && (
            <Input
              label="Bank Branch"
              value={state.branchName ?? ""}
              onChange={(e) => handleChanges({ branchName: e.target.value })}
              placeholder="Bank branch"
            />
          )}

          <InputGroup>
            <Input
              w="100%"
              label="Account Number"
              value={state.bankAccountNumber ?? ""}
              onChange={handleAccountNumber}
              placeholder="Account number"
            />
            {country === "nigeria" && (
              <InputRightElement
                h="100%"
                mr="6px"
                mt="14px"
                children={
                  state?.bankCode &&
                  state?.bankAccountNumber?.length >= 10 && (
                    <IconButton
                      isLoading={isResolvingBank}
                      size="xs"
                      aria-label="reload"
                      onClick={() =>
                        resolveBank({
                          bankCode: state.bankCode,
                          bankAccountNumber: state.bankAccountNumber,
                        })
                      }
                      _focus={{
                        shadow: "0 0 0 3px #06C3448f",
                      }}
                      _active={{
                        shadow: "0 0 0 3px #06C3448f",
                      }}
                      icon={<ReplayRounded style={{ fontSize: "18px" }} />}
                    />
                  )
                }
              />
            )}
          </InputGroup>
          <Input
            disabled={country === "nigeria"}
            label="Account Name"
            value={state.bankAccountName ?? ""}
            onChange={(e) => handleChanges({ bankAccountName: e.target.value })}
            placeholder="Account name"
          />

          <Button
            disabled={isDisabled()}
            isLoading={isAddingBank}
            onClick={handleAddBank}
            h="56px"
          >
            Add Bank
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

const BankList = () => {
  const toast = useToast();
  const {
    data: accData,
    isLoading,
    error,
    isError,
    refetch,
  } = useGetBanksDetailsQuery();

  const data = useMemo(
    () => (accData ?? []).filter((d) => d?.type !== "mobilemoney"),
    [accData]
  );

  console.log(data, "bank details");

  const toDelete = useRef(-1);

  const [
    deleteBank,
    {
      isLoading: isDeleting,
      error: deleteError,
      isError: isDeleteError,
      isSuccess,
    },
  ] = useDeleteBankMutation();

  useEffect(() => {
    if (isDeleteError && deleteError)
      toast({
        position: "bottom-right",
        title: "Oops an error occurred",
        description: deleteError ?? "Service unavailable, try again later",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
  }, [isDeleteError, deleteError, toast]);

  useEffect(() => {
    if (isError && error)
      toast({
        position: "bottom-right",
        title: "Oops an error occurred",
        description:
          error?.data?.error ?? "Unable to fetch bank list, try again later",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
  }, [isError, error, toast]);

  useEffect(() => {
    if (isSuccess) refetch();
  }, [isSuccess, refetch]);

  const handleDelete = (id, index) => {
    toDelete.current = index;
    deleteBank(id);
  };

  return (
    <Box mt="20px">
      <Container className="user-bank" maxW="sm">
        <Stack className="user-bank__row" gridGap="20px">
          {data?.map((bank, i) => (
            <Box key={i} className="flexBasis">
              <Box
                w="100%"
                flexDir="column"
                alignItems="flex-start"
                justifyContent="flex-end"
                className="asr-bank-box"
              >
                <Box as="div" width="100%" textAlign="right">
                  {bank.default && (
                    <div>
                      <CheckCircleOutline className="checkCircleOutline" />
                    </div>
                  )}
                </Box>
                <Box>
                  <Heading fontSize=".9em" textTransform="uppercase">
                    {bank.bankAccountName}
                  </Heading>
                </Box>
                <HStack
                  w="100%"
                  justifyContent="space-between"
                  className="asr-bank-number"
                >
                  <p> {bank.bankName}</p>
                  <p>{bank.bankAccountNumber}</p>
                </HStack>
              </Box>
              <Button
                isLoading={toDelete.current === i && isDeleting}
                w="100%"
                h="56px"
                bg="#da2c71"
                mt="16px"
                disabled={toDelete.current === i && isDeleting}
                onClick={() => handleDelete(bank._id, i)}
              >
                Delete Bank
              </Button>
            </Box>
          ))}

          {!data && (
            <VStack justifyContent="center" mt="60px">
              <IconButton
                isLoading={isLoading}
                as={Button}
                color="grey"
                size="lg"
                borderRadius="50%"
                bg="white"
                _focus={{
                  boxShadow: "0 0 0 3px #06C3448f, 0 0 10px rgba(0,0,0,.05)",
                  transition: "all 300ms ease-in-out",
                }}
                aria-label="reload banks"
                shadow="base"
                icon={<ReplayRounded />}
                disabled={isLoading}
                onClick={() => refetch()}
              />
              {!isLoading && (
                <Box pointerEvents="none" color="grey">
                  Refetch banks
                </Box>
              )}
            </VStack>
          )}

          {data && isEmpty(data) && (
            <VStack justifyContent="center" mt="60px" color="grey">
              <Box as={NoDataIcon} fontSize="8em" />
              {!isLoading && (
                <Box pointerEvents="none" color="currentColor">
                  Click the <strong>+ Add Bank </strong>button to add a bank
                </Box>
              )}
            </VStack>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

class AccountBanks extends Component {
  state = {
    unVerifiedShow: true,
    bankSection: true,
    cardSection: false,
    changePassword: false,
    userData: "",
    userBankData: [],
    tokenError: "",
    isLoaded: false,
    cardAddError: false,
    allBanks: undefined,
  };

  handleBank = (event) => {
    event.preventDefault();

    this.setState({
      bankSection: true,
      addBankSection: false,
      cardSection: false,
      changePassword: false,
    });
  };
  handleBankAdd = (event) => {
    event.preventDefault();

    this.setState({
      bankSection: false,
      addBankSection: true,
      cardSection: false,
      changePassword: false,
    });

    // this.loadBanks()
  };
  handleCard = (event) => {
    event.preventDefault();

    this.setState({
      bankSection: false,
      addBankSection: false,
      cardSection: true,
      changePassword: false,
    });

    this.props?.history && this.props?.history.push("/dashboard/cards");
  };

  handleCP = (event) => {
    event.preventDefault();

    this.setState({
      bankSection: false,
      addBankSection: false,
      cardSection: false,
      changePassword: true,
    });
  };

  loadBankDetails = async () => {
    try {
      const token = localStorage.getItem("tokenset");

      const headers = {
        authorization: `${token}`,
        "Content-Type": "application/json",
      };

      const getBankReq = await get(
        `${process.env.REACT_APP_API_ENDPOINT}/account/getBankDetails`,
        headers
      );

      const userBankData = getBankReq.data.data.bank;

      this.setState({
        userBankData,
        userBankDataLength: getBankReq.data.data.bank.length,
        isLoaded: true,
      });

      //logger(this.state.userBankData[0].bankAccountName)

      return Promise.resolve(true);
    } catch (err) {
      if (err.isAxiosError) {
        const errData = err.response;
        if (errData?.status === 403 || errData?.status === 401) {
          const errmsg = document.getElementById("error-info2");
          errmsg.classList.add("err-auth-on");
          this.setState({
            incorrectError: "Token expired, please login again.",
          });

          setTimeout(() => {
            window.location = "/";
          }, 2000);
        }
      }
      return Promise.reject(err);
    }
  };

  loadAllBanks = async () => {
    try {
      const token = localStorage.getItem("tokenset");

      const headers = {
        authorization: `${token}`,
        "Content-Type": "application/json",
      };

      const getBankReq = await get(
        `${process.env.REACT_APP_API_ENDPOINT}/banks`,
        headers
      );

      const allBanks = getBankReq.data.data.bankPayloads;

      return allBanks;
    } catch (err) {}
  };

  deleteBankDetails = async (event) => {
    try {
      event.preventDefault();
      // load gif
      const loaded = document.getElementById("load");
      loaded.classList.add("load-gif-on2");

      const token = localStorage.getItem("tokenset");

      // console.log(reqHeaders)

      const delBankReq = await del(
        `${process.env.REACT_APP_API_ENDPOINT}/account/deleteBankDetails`,
        {
          authorization: `${token}`,
          "Content-Type": "application/json",
        }
      );

      // load gif remove
      loaded.classList.remove("load-gif-on2");
      window.location = "/dashboard/accountbanks";

      logger(delBankReq.data);
    } catch (err) {
      logger("update ERROR::: ", err);
      if (err.isAxiosError) {
        const errData = err.response;
        if (errData?.status === 403 || errData?.status === 401) {
          const errmsg = document.getElementById("error-info2");
          errmsg.classList.add("err-auth-on");
          this.setState({
            incorrectError: "Token expired, please login again.",
          });

          setTimeout(() => {
            window.location = "/";
          }, 2000);
        }
      }
    }
  };

  componentDidMount() {
    localStorage.setItem("changePinStatus", false);

    this.setState({
      userData: this.props.userData,
      // userBankData,
    });

    this.loadBankDetails();
  }

  // Add bank function
  bankAddFunction = async (event) => {
    try {
      event && event.preventDefault();
      let allBanksFromServer = [];
      const allBanks = this.state.allBanks;
      allBanks
        ? (allBanksFromServer = allBanks)
        : (allBanksFromServer = await this.loadAllBanks());
      // cache the data;
      this.setState({ allBanks: allBanksFromServer });

      // maybe show some error. we expect this data to be an array
      if (!Array.isArray(allBanksFromServer)) {
        this.setState({
          incorrectError: "Unable to validate bank code",
        });
        return;
      }

      // load gif
      if (this.state.bankName !== undefined) {
        const loaded = document.getElementById("load");
        loaded.classList.add("load-gif-on2");

        const matchingBank = allBanksFromServer.find(
          (bank) =>
            bank &&
            bank.bankName &&
            this.state.bankName &&
            bank.bankName.trim().toLowerCase() ===
              this.state.bankName.trim().toLowerCase()
        );
        const bankCode = matchingBank ? matchingBank.bankCode : "";

        if (!bankCode) {
          this.setState({
            incorrectError:
              "Bank code is rejected by server. Kindly report the problem to our support ",
          });

          // load gif remove
          loaded.classList.remove("load-gif-on2");
          return;
        }

        const token = localStorage.getItem("tokenset");

        const headers = {
          authorization: `${token}`,
          "Content-Type": "application/json",
        };
        const bankDetails = {
          bankName: this.state.bankName,
          bankCode,
          bankAccountNumber: this.state.bankAccountNumber,
        };

        const addBankReq = await post(
          `${process.env.REACT_APP_API_ENDPOINT}/account/addBankDetails`,
          bankDetails,
          headers
        );

        // load gif remove
        loaded.classList.remove("load-gif-on2");

        this.setState({
          successMsg: "Bank details successfully added.",
          incorrectError: " ",
        });

        const message = document.getElementById("successmsg");
        setTimeout(() => {
          message.classList.add("message-off");
          window.location = "/dashboard/accountbanks";
        }, 2500);

        logger(addBankReq.data);
      } else {
        this.setState({
          incorrectError: "Please select a bank.",
        });
      }
    } catch (err) {
      logger("update ERROR::: ", err);
      try {
        if (err.isAxiosError) {
          const errData = err.response;
          if (errData?.status === 403 || errData?.status === 401) {
            const errmsg = document.getElementById("error-info2");
            errmsg.classList.add("err-auth-on");
            this.setState({
              incorrectError: "Token expired, please login again.",
            });

            setTimeout(() => {
              window.location = "/";
            }, 2000);

            if (
              errData.data.error ===
              "bank account name does not matches user's first and last name"
            ) {
              this.setState({
                incorrectError:
                  "Please make sure bank account name matches your user's first and last name.",
              });
            }
            if (
              errData.data.error !==
              "bank account name does not matches user's first and last name"
            ) {
              this.setState({
                incorrectError: "Token expired, please login again.",
              });

              setTimeout(() => {
                window.location = "/";
              }, 2000);

              if (
                errData.data.error ===
                "bank account name does not matches user's first and last name"
              ) {
                this.setState({
                  incorrectError:
                    "Please make sure bank account name matches your user's first and last name.",
                });
              }
              if (
                errData.data.error !==
                "bank account name does not matches user's first and last name"
              ) {
                this.setState({
                  incorrectError: errData.data.error,
                });
              }
            }
          }
          logger(err.response.data.error);
          this.setState({
            incorrectError: err.response.data.error,
          });
          const loaded = document.getElementById("load");
          loaded.classList.remove("load-gif-on2");
        }
      } catch (err) {}
    }
  };

  render() {
    const currentPage = "Account";

    return (
      <div>
        <Sidemenu currentPage={currentPage} logoutUser={this.props.logout} />
        <Header />
        <section>
          <div className="account-container">
            {/* {this.state.unVerifiedShow ? (
              <div className="home-verifywarn-m">
                <p className="h-v-m"> You need to verify your account </p>
                <Link to="/dashboard/accountverify" className="verify-m h-v-m">
                  <p>Verify now</p>
                </Link>
              </div>
            ) : null} */}

            {/* {this.state.isLoaded ? ( */}
            <div className="account-content">
              <div className="account-top" style={{ display: "flex" }}>
                <Heading className="b-header">Account</Heading>
                <Link className="m-header" to="/dashboard/account">
                  <img src={Back} className="w-backarrow" alt="back" />
                </Link>
                <h1 className="m-header"> Banks and Cards </h1>
              </div>
              <div className="account-nav animate__animated animate__fadeInDown">
                <Link to="/dashboard/account" className="right-e">
                  <p className="ash"> Account </p>{" "}
                </Link>
                <img className="right-e" src={Arrow} alt="" />
                {this.state.bankSection === true && (
                  <p>
                    <span className="bold"> Banks</span>
                  </p>
                )}
                {this.state.cardSection === true && (
                  <p>
                    <span className="bold"> Cards</span>
                  </p>
                )}
              </div>
              <div className="account-sections">
                <div className="account-section-left section-hide">
                  <div className="asl-content">
                    <div className="asl-actions">
                      <AccountSidebar
                        current="banks"
                        userData={this.props.userData}
                        logout={this.props.logout}
                      />
                    </div>
                  </div>
                </div>
                <div className="account-section-right animate__animated animate__fadeIn">
                  <div className="asr-edit">
                    {this.state.cardSection === true && <h2>Cards</h2>}
                    {this.state.bankSection === true && <h2>Banks</h2>}
                    {this.state.cardSection === true && (
                      <div className="asr-tabs-sm">
                        <div
                          onClick={this.handleBank}
                          className="asr-tab-single"
                        >
                          <p>Banks</p>
                        </div>
                        <div
                          onClick={this.handleCard}
                          className="asr-tab-single active-tab"
                        >
                          <p>Cards</p>
                        </div>
                      </div>
                    )}
                    {this.state.bankSection === true && (
                      <div className="asr-tabs-sm">
                        <div
                          onClick={this.handleBank}
                          className="asr-tab-single active-tab"
                        >
                          <p>Banks</p>
                        </div>
                        <div
                          onClick={this.handleCard}
                          className="asr-tab-single "
                        >
                          <p className="disable-value">Cards</p>
                        </div>
                      </div>
                    )}
                    {this.state.addBankSection === true && (
                      <div className="asr-tabs-sm">
                        <div
                          onClick={this.handleBank}
                          className="asr-tab-single active-tab"
                        >
                          <p>Banks</p>
                        </div>
                        <div className="asr-tab-single ">
                          <p className="disable-value">Cards</p>
                        </div>
                      </div>
                    )}
                    {this.state.bankSection === true && (
                      <div className="asr-bc-content">
                        {/* {this.state.userBankDataLength < 1 &&  ( */}
                        <div className="asr-add" onClick={this.handleBankAdd}>
                          <img className="right-m1" src={Add} alt="add" />
                          <p style={{ cursor: "pointer" }}> Add Bank </p>
                        </div>

                        <BankList />
                      </div>
                    )}
                    {this.state.cardSection === true && (
                      <div className="asr-bc-content">
                        <div className="asr-add" style={{ cursor: "pointer" }}>
                          <img className="right-m1" src={Add} alt="add" />
                          <p> Add Cards </p>
                        </div>
                        <div className="asr-card-box">
                          <div className="asr-card-top">
                            <img src={Master} alt="mastercard" />
                          </div>
                          <div className="asr-card-info">
                            <p>xxxx xxxx xxxx 1289</p>
                            <p>09/25</p>
                          </div>
                        </div>
                        <div className="asr-card-desc">
                          <div className="asr-card-descsingle">
                            <p>
                              <span>Issuer Bank</span>{" "}
                            </p>
                            <p> Guaranty Trust Bank</p>
                          </div>
                          <div className="asr-card-descsingle">
                            <p>
                              <span>Added On</span>{" "}
                            </p>
                            <p> May 03, 2020</p>
                          </div>
                          <div className="asr-card-descsingle">
                            <p>
                              <span>Expires in 570 days</span>{" "}
                            </p>
                          </div>
                        </div>
                        <button className="asr-bank-delete">
                          {" "}
                          Delete Card
                        </button>
                      </div>
                    )}
                    {this.state.addBankSection === true && (
                      <AddBankForm
                        onAdded={() =>
                          this.setState({
                            bankSection: true,
                            addBankSection: false,
                            cardSection: false,
                            changePassword: false,
                          })
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default function AccountBanksWapper(props) {
  const history = useHistory();

  return <AccountBanks {...props} history={history} />;
}
